import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye,
  ShieldCheck,
  Users,
  Activity,
  TrendingUp,
  Sparkles,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth';
import type { Business } from '@nako/types';

interface Row {
  id: string;
  name: string;
  owner_id: string;
  owner_email: string | null;
  industry: string | null;
  tier: string;
  is_demo: boolean;
  created_at: string;
  je_count: number;
  receipts_count: number;
  invoices_count: number;
  last_activity: string | null;
}

interface ActivityEvent {
  kind: 'signup' | 'journal' | 'receipt' | 'invoice';
  business_name: string;
  business_id: string;
  description: string;
  at: string;
}

const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;
const ONE_MONTH = 30 * 24 * 60 * 60 * 1000;

export default function Admin() {
  const profile = useAuthStore((s) => s.profile);
  const setBusiness = useAuthStore((s) => s.setBusiness);
  const navigate = useNavigate();

  const [rows, setRows] = useState<Row[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [showDemos, setShowDemos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile && !profile.is_admin) {
      navigate('/app');
    }
  }, [profile, navigate]);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data: businesses, error: bizErr } = await supabase
        .from('businesses')
        .select('id, name, owner_id, industry, tier, is_demo, created_at')
        .order('created_at', { ascending: false });
      if (bizErr) throw bizErr;

      const ownerIds = Array.from(new Set((businesses ?? []).map((b) => b.owner_id)));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email')
        .in('id', ownerIds);
      const emailById = new Map((profiles ?? []).map((p) => [p.id, p.email]));

      const ids = (businesses ?? []).map((b) => b.id);

      const [{ data: jeRows }, { data: receiptRows }, { data: invoiceRows }] = await Promise.all([
        supabase.from('journal_entries').select('business_id, created_at').in('business_id', ids),
        supabase
          .from('receipts')
          .select('business_id, uploaded_at, merchant_name')
          .in('business_id', ids),
        supabase
          .from('invoices')
          .select('business_id, created_at, customer_name, total')
          .in('business_id', ids),
      ]);

      const counts = new Map<
        string,
        { je: number; receipts: number; invoices: number; latest: string | null }
      >();
      const bump = (b: string, latestCandidate: string | null) => {
        const e = counts.get(b) ?? { je: 0, receipts: 0, invoices: 0, latest: null };
        counts.set(b, e);
        if (latestCandidate && (!e.latest || latestCandidate > e.latest))
          e.latest = latestCandidate;
        return e;
      };
      for (const r of jeRows ?? []) bump(r.business_id, r.created_at).je += 1;
      for (const r of receiptRows ?? []) bump(r.business_id, r.uploaded_at).receipts += 1;
      for (const r of invoiceRows ?? []) bump(r.business_id, r.created_at).invoices += 1;

      const built: Row[] = (businesses ?? []).map((b) => {
        const c = counts.get(b.id) ?? { je: 0, receipts: 0, invoices: 0, latest: null };
        return {
          id: b.id,
          name: b.name,
          owner_id: b.owner_id,
          owner_email: emailById.get(b.owner_id) ?? null,
          industry: b.industry,
          tier: b.tier,
          is_demo: b.is_demo,
          created_at: b.created_at,
          je_count: c.je,
          receipts_count: c.receipts,
          invoices_count: c.invoices,
          last_activity: c.latest ?? b.created_at,
        };
      });
      built.sort((a, b) => (b.last_activity ?? '').localeCompare(a.last_activity ?? ''));
      setRows(built);

      const events: ActivityEvent[] = [];
      const nameById = new Map(built.map((r) => [r.id, r.name]));
      for (const b of businesses ?? []) {
        events.push({
          kind: 'signup',
          business_id: b.id,
          business_name: b.name,
          description: `New signup — ${emailById.get(b.owner_id) ?? 'unknown'}`,
          at: b.created_at,
        });
      }
      for (const r of jeRows ?? []) {
        events.push({
          kind: 'journal',
          business_id: r.business_id,
          business_name: nameById.get(r.business_id) ?? '',
          description: 'Journal entry posted',
          at: r.created_at,
        });
      }
      for (const r of receiptRows ?? []) {
        events.push({
          kind: 'receipt',
          business_id: r.business_id,
          business_name: nameById.get(r.business_id) ?? '',
          description: `Receipt scanned${r.merchant_name ? ` — ${r.merchant_name}` : ''}`,
          at: r.uploaded_at,
        });
      }
      for (const r of invoiceRows ?? []) {
        events.push({
          kind: 'invoice',
          business_id: r.business_id,
          business_name: nameById.get(r.business_id) ?? '',
          description: `Invoice issued${r.customer_name ? ` to ${r.customer_name}` : ''}${r.total ? ` (P ${Number(r.total).toFixed(2)})` : ''}`,
          at: r.created_at,
        });
      }
      events.sort((a, b) => b.at.localeCompare(a.at));
      setActivity(events.slice(0, 30));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!profile?.is_admin) return;
    void load();
  }, [profile?.is_admin]);

  const visibleRows = useMemo(() => rows.filter((r) => showDemos || !r.is_demo), [rows, showDemos]);
  const visibleEvents = useMemo(() => {
    const allow = new Set(visibleRows.map((r) => r.id));
    return activity.filter((e) => allow.has(e.business_id));
  }, [activity, visibleRows]);

  const now = Date.now();
  const realRows = rows.filter((r) => !r.is_demo);
  const totalCustomers = realRows.length;
  const activeWeek = realRows.filter(
    (r) => r.last_activity && now - new Date(r.last_activity).getTime() < ONE_WEEK,
  ).length;
  const signupsMonth = realRows.filter(
    (r) => now - new Date(r.created_at).getTime() < ONE_MONTH,
  ).length;
  const totalJes = realRows.reduce((acc, r) => acc + r.je_count, 0);

  const viewAs = async (businessId: string) => {
    const { data: biz } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', businessId)
      .single();
    if (!biz) return;
    sessionStorage.setItem('np.adminViewAs', biz.id);
    setBusiness(biz as Business);
    navigate('/app');
  };

  if (!profile) return <main className="p-6 text-sm">Loading…</main>;
  if (!profile.is_admin) return null;

  const fmtRel = (iso: string | null) => {
    if (!iso) return '—';
    const diff = now - new Date(iso).getTime();
    const d = Math.floor(diff / 86_400_000);
    if (d === 0) return 'today';
    if (d === 1) return 'yesterday';
    if (d < 7) return `${d}d ago`;
    if (d < 30) return `${Math.floor(d / 7)}w ago`;
    return iso.slice(0, 10);
  };

  const eventTone: Record<ActivityEvent['kind'], string> = {
    signup: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    journal: 'bg-blue-50 text-blue-700 border-blue-200',
    receipt: 'bg-purple-50 text-purple-700 border-purple-200',
    invoice: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <main className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-amber-600" size={22} />
          <h1 className="text-xl font-semibold">Nako Tech — Admin</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDemos((s) => !s)}
            className="border-line hover:bg-paper-2 inline-flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs"
          >
            <Filter size={12} /> {showDemos ? 'Hide demos' : 'Show demos'}
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={refreshing}
            className="border-line hover:bg-paper-2 inline-flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs disabled:opacity-60"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-muted mb-6 text-xs">
        Signed in as <strong>{profile.email}</strong> · viewing all customers ·
        {showDemos ? ' demos shown' : ' demos hidden'}
      </p>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          icon={<Users size={16} />}
          label="Active customers"
          value={totalCustomers}
          tone="emerald"
        />
        <KpiCard
          icon={<Activity size={16} />}
          label="Active this week"
          value={activeWeek}
          tone="blue"
        />
        <KpiCard
          icon={<Sparkles size={16} />}
          label="New (30 days)"
          value={signupsMonth}
          tone="amber"
        />
        <KpiCard
          icon={<TrendingUp size={16} />}
          label="Journal entries posted"
          value={totalJes}
          tone="purple"
        />
      </div>

      {loading ? (
        <p className="text-muted text-sm">Loading customers…</p>
      ) : error ? (
        <p className="text-sm text-red-600">Error: {error}</p>
      ) : (
        <>
          <h2 className="text-muted mb-2 text-sm font-semibold uppercase tracking-wide">
            Customers ({visibleRows.length})
          </h2>
          <div className="border-line overflow-x-auto rounded-md border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-paper-2 text-left text-xs uppercase">
                <tr>
                  <th className="p-3">Business</th>
                  <th className="p-3">Owner</th>
                  <th className="p-3">Industry</th>
                  <th className="p-3">Tier</th>
                  <th className="p-3">JEs</th>
                  <th className="p-3">Receipts</th>
                  <th className="p-3">Invoices</th>
                  <th className="p-3">Last activity</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr key={r.id} className="border-line border-t">
                    <td className="p-3 font-medium">
                      {r.name}
                      {r.is_demo && (
                        <span className="bg-paper-2 text-muted ml-2 rounded px-1.5 py-0.5 text-[10px] uppercase">
                          demo
                        </span>
                      )}
                    </td>
                    <td className="text-muted p-3">{r.owner_email ?? '—'}</td>
                    <td className="text-muted p-3">{r.industry ?? '—'}</td>
                    <td className="text-muted p-3">{r.tier}</td>
                    <td className="text-muted p-3">{r.je_count}</td>
                    <td className="text-muted p-3">{r.receipts_count}</td>
                    <td className="text-muted p-3">{r.invoices_count}</td>
                    <td className="text-muted p-3">{fmtRel(r.last_activity)}</td>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() => void viewAs(r.id)}
                        className="border-line hover:bg-paper-2 inline-flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs"
                      >
                        <Eye size={12} /> View as
                      </button>
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-muted p-6 text-center">
                      No customers yet.{' '}
                      {!showDemos && 'Toggle "Show demos" to see the seeded demo accounts.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h2 className="text-muted mb-2 mt-8 text-sm font-semibold uppercase tracking-wide">
            Recent activity ({visibleEvents.length})
          </h2>
          <div className="border-line rounded-md border bg-white">
            {visibleEvents.length === 0 ? (
              <p className="text-muted p-6 text-center text-sm">No activity to show yet.</p>
            ) : (
              <ul>
                {visibleEvents.map((e, i) => (
                  <li
                    key={`${e.kind}-${e.at}-${i}`}
                    className="border-line flex items-center justify-between gap-3 border-b p-3 last:border-b-0"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase ${eventTone[e.kind]}`}
                      >
                        {e.kind}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm">
                          <strong>{e.business_name}</strong> · {e.description}
                        </p>
                      </div>
                    </div>
                    <span className="text-muted shrink-0 text-xs">{fmtRel(e.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <p className="text-muted mt-8 text-xs">
        <Link to="/app" className="underline">
          ← Back to dashboard
        </Link>
      </p>
    </main>
  );
}

function KpiCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: 'emerald' | 'blue' | 'amber' | 'purple';
}) {
  const toneClasses = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    purple: 'border-purple-200 bg-purple-50 text-purple-700',
  };
  return (
    <div className="border-line rounded-md border bg-white p-3">
      <div
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase ${toneClasses[tone]}`}
      >
        {icon}
        {label}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
